import { HStack, Radio, RadioGroup, Stack, Text } from '@chakra-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreState } from '../../../../../../models/hooks';
import { SCREENING_TYPE_LABELS } from '../../../questionnaireTemplate.constants';
import { SCREENING_TYPE } from '../../../questionnaireTemplate.enums';
// eslint-disable-next-line import/no-cycle
import { QuestionnaireTemplateStore } from '../../../store/questionnaireTemplateModel';

const SCREENING_TYPE_PATH = 'screeningType';

interface ScreeningTypeProps {
  id: string;
  screeningTypes: SCREENING_TYPE[];
}

function useFilteredScreeningTypes(
  screeningTypes: SCREENING_TYPE[],
  isNeedReviewEnabled: boolean | undefined,
  currentValue: string,
): SCREENING_TYPE[] {
  return React.useMemo(() => {
    if (isNeedReviewEnabled || currentValue === SCREENING_TYPE.REVIEW) {
      return screeningTypes;
    }
    return screeningTypes.filter((type) => type !== SCREENING_TYPE.REVIEW);
  }, [screeningTypes, isNeedReviewEnabled, currentValue]);
}

export const ScreeningType = React.memo(function ScreeningType({ id, screeningTypes }: ScreeningTypeProps) {
  const { t } = useTranslation('questionnaireTemplate');

  const isNeedReviewEnabled = useStoreState((s) => s.app.accounts?.configuration?.needReviewEnabled);

  const value = QuestionnaireTemplateStore.useStoreState((s) => s.getElementValue(id, SCREENING_TYPE_PATH));
  const updateValue = QuestionnaireTemplateStore.useStoreActions((a) => a.updateElementValue);

  const filteredScreeningTypes = useFilteredScreeningTypes(screeningTypes, isNeedReviewEnabled, value);
  const screeningTypeLabels = React.useMemo(() => SCREENING_TYPE_LABELS(t), [t]);

  const handleChange = React.useCallback(
    (next: string): void => {
      updateValue({ id, path: SCREENING_TYPE_PATH, value: next });
    },
    [id, updateValue],
  );

  return (
    <Stack>
      <Text fontSize="sm">{t('formBuilder.canvas.element.screeningTypes.label')}</Text>
      <RadioGroup value={value} onChange={handleChange} colorScheme="blue">
        <HStack spacing={8}>
          {filteredScreeningTypes.map((type) => (
            <Radio key={type} value={type} cursor="pointer">
              <Text fontSize="sm">{screeningTypeLabels[type]}</Text>
            </Radio>
          ))}
        </HStack>
      </RadioGroup>
    </Stack>
  );
});

ScreeningType.displayName = 'ScreeningType';
